import { codeExample, defineDemoElement } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import type { DemoSection } from './demo-shared.js';

const COLOR_SCALES = [
  'primary',
  'success',
  'warning',
  'danger',
  'neutral',
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose'
];

const COLOR_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

/** Every icon name resolvable by the built-in (default) icon library. */
const DEFAULT_ICONS = [
  'adjust',
  'bin',
  'caret',
  'caret-up',
  'check',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevron-up',
  'copy',
  'edit',
  'eye',
  'eye-slash',
  'indeterminate',
  'no-tax',
  'person',
  'radio',
  'x-circle-fill',
  'x-lg'
];

class DemoColorScales extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .scale {
      display: grid;
      grid-template-columns: 6rem repeat(11, minmax(2rem, 1fr));
      align-items: center;
      gap: 2px;
    }

    .scale + .scale {
      margin-top: var(--webmodule-spacing-x-small);
    }

    .scale-name {
      font-family: var(--webmodule-font-mono);
      font-size: var(--webmodule-font-size-x-small);
      color: var(--webmodule-color-neutral-700);
    }

    .swatch {
      height: 2rem;
      border-radius: var(--webmodule-border-radius-small);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: var(--webmodule-font-size-2x-small);
    }

    .steps {
      display: grid;
      grid-template-columns: 6rem repeat(11, minmax(2rem, 1fr));
      gap: 2px;
      margin-bottom: var(--webmodule-spacing-x-small);
      font-size: var(--webmodule-font-size-2x-small);
      color: var(--webmodule-color-neutral-500);
      text-align: center;
    }
  `;

  render() {
    return html`
      <div class="steps">
        <span></span>
        ${COLOR_STEPS.map(step => html`<span>${step}</span>`)}
      </div>
      ${COLOR_SCALES.map(
        scale => html`
          <div class="scale">
            <span class="scale-name">${scale}</span>
            ${COLOR_STEPS.map(
              step => html`<div class="swatch" style="background: var(--webmodule-color-${scale}-${step})"></div>`
            )}
          </div>
        `
      )}
    `;
  }
}

class DemoIconGallery extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .gallery {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
      gap: var(--webmodule-spacing-small);
    }

    .item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--webmodule-spacing-small);
      padding: var(--webmodule-spacing-medium);
      border: solid 1px var(--webmodule-color-neutral-200);
      border-radius: var(--webmodule-border-radius-medium);
    }

    webmodule-icon {
      font-size: 1.5rem;
    }

    .name {
      font-family: var(--webmodule-font-mono);
      font-size: var(--webmodule-font-size-2x-small);
      color: var(--webmodule-color-neutral-600);
    }
  `;

  render() {
    return html`
      <div class="gallery">
        ${DEFAULT_ICONS.map(
          name => html`
            <div class="item">
              <webmodule-icon name=${name} label=${name}></webmodule-icon>
              <span class="name">${name}</span>
            </div>
          `
        )}
      </div>
    `;
  }
}

export function registerFoundationDemos() {
  defineDemoElement('demo-color-scales', DemoColorScales);
  defineDemoElement('demo-icon-gallery', DemoIconGallery);
}

export const colorsSection: DemoSection = {
  id: 'colors',
  title: 'Color Tokens',
  summary:
    'Every component is themed through --webmodule-* custom properties. The color scales below are the raw palette; ' +
    'semantic aliases (primary, success, warning, danger, neutral) map onto them per theme. Switch the theme in the ' +
    'header to see the scales invert for dark mode.',
  render: () => html`
    <demo-example
      label="Palette"
      .code=${'/* Use tokens, never raw hex values: */\ncolor: var(--webmodule-color-primary-600);\nbackground: var(--webmodule-color-neutral-50);'}
    >
      <div slot="description">
        Each scale runs from 50 (lightest) to 950 (darkest) in the light themes; dark mode remaps the same custom
        properties so token-based styling adapts automatically.
      </div>
      <demo-color-scales></demo-color-scales>
    </demo-example>
  `
};

export const iconsSection: DemoSection = {
  id: 'icon',
  title: 'Icon',
  tags: ['webmodule-icon'],
  summary:
    'Icons render inline SVG from the built-in icon library and inherit the current text color. Size them with ' +
    'font-size and always provide a label when the icon conveys meaning on its own.',
  render: () => html`
    <demo-example label="Built-in library" .code=${'<webmodule-icon name="copy" label="Copy"></webmodule-icon>'}>
      <div slot="description">All names resolvable by the default icon library.</div>
      <demo-icon-gallery></demo-icon-gallery>
    </demo-example>

    ${codeExample({
      label: 'Sizing and color',
      description: 'Icons scale with font-size and follow currentColor.',
      code: `
        <webmodule-icon name="eye" style="font-size: 1rem;"></webmodule-icon>
        <webmodule-icon name="eye" style="font-size: 1.5rem;"></webmodule-icon>
        <webmodule-icon name="eye" style="font-size: 2rem;"></webmodule-icon>
        <webmodule-icon name="eye" style="font-size: 2rem; color: var(--webmodule-color-primary-600);"></webmodule-icon>
        <webmodule-icon name="eye" style="font-size: 2rem; color: var(--webmodule-color-danger-600);"></webmodule-icon>`
    })}
  `
};
